import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ConfirmDialog, Tooltip, useToast } from "@/shared/ui";
import { CheckIcon, CopyIcon, ForkIcon, RegenerateIcon } from "./icons";

export interface MessageActionsProps {
  /** Raw text to copy. */
  text: string;
  onRegenerate?: () => void;
  onFork?: () => void;
}

const actionClass =
  "grid size-[30px] place-items-center rounded-[8px] text-ink-soft outline-none transition-colors duration-150 ease-house hover:bg-line-soft hover:text-ink focus-visible:bg-line-soft [&_svg]:size-4";

/**
 * Hover-revealed per-message actions (DESIGN §5): copy, regenerate, fork. All
 * fully wired (copy → clipboard, regenerate → new sibling run, fork → new chat).
 * Each control carries a delayed hover tooltip; copy also fires a toast.
 */
export function MessageActions({ text, onRegenerate, onFork }: MessageActionsProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);
  const [confirmFork, setConfirmFork] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast(t("messages.copied"));
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast(t("messages.couldntCopy"));
    }
  }

  return (
    <div className="flex gap-0.5">
      <Tooltip label={t("messages.copy")}>
        <button type="button" aria-label={t("messages.copy")} onClick={copy} className={actionClass}>
          {copied ? <CheckIcon /> : <CopyIcon />}
        </button>
      </Tooltip>
      {onRegenerate ? (
        <Tooltip label={t("messages.retryResponse")}>
          <button
            type="button"
            data-testid="retry-message-button"
            aria-label={t("messages.retryResponse")}
            onClick={onRegenerate}
            className={actionClass}
          >
            <RegenerateIcon />
          </button>
        </Tooltip>
      ) : null}
      {onFork ? (
        <Tooltip label={t("messages.createNewChatFromHere")}>
          <button
            type="button"
            aria-label={t("messages.createNewChatFromHere")}
            onClick={() => setConfirmFork(true)}
            className={actionClass}
          >
            <ForkIcon />
          </button>
        </Tooltip>
      ) : null}
      {onFork ? (
        <ConfirmDialog
          open={confirmFork}
          title={t("messages.forkConfirmTitle")}
          message={t("messages.forkConfirmBody")}
          confirmLabel={t("messages.forkConfirmCta")}
          cancelLabel={t("common.cancel")}
          onConfirm={() => {
            setConfirmFork(false);
            onFork();
          }}
          onCancel={() => setConfirmFork(false)}
        />
      ) : null}
    </div>
  );
}
